// ============================================================================
// PDF Export V1 — rich jsPDF vector layout (cover, prompt, stats, samples).
// ============================================================================
// Theme-independent: V1 uses hardcoded ESKIM_COLORS values and jsPDF
// programmatic rendering (no HTML/CSS capture). Output is pixel-identical
// across user themes. See deepcontext-fgvn for related V2 html2canvas
// light-theme pinning.
// ============================================================================
// DO NOT FLATTEN INTO V2 (pdfExportUtilsV2.ts) WITHOUT AN EXPLICIT DECISION.
//
// Customer workflows email/archive these reports; the "Export PDF" button in
// PromptExport.tsx and BulkJobExport.tsx wires to `generateAnalysisReport`
// below and MUST keep producing the v1.12 layout. The brand-forward v2 layout
// lives in `pdfExportUtilsV2.ts` and is exposed via a separate "Export PDF 2
// (beta)" button. See bead deepcontext-7u2ox for the regression history.
// ============================================================================

import jsPDF from 'jspdf';

// jsPDF's built-in fonts lack metrics for extended Latin characters (Czech, Polish, Turkish, etc.).
// Step 1: NFD-decompose, then strip combining marks (handles ě→e, š→s, ç→c, ş→s, ğ→g, ö→o, …).
// Step 2: replace non-decomposing extended-Latin codepoints — these have their own codepoints
// and survive NFD, so they need an explicit ASCII fallback (Turkish ı was the trigger here).
const NON_DECOMPOSING_LATIN_MAP: Record<string, string> = {
    '\u0131': 'i', '\u0130': 'I',     // Turkish dotless i / dotted I
    '\u0142': 'l', '\u0141': 'L',     // Polish
    '\u00f8': 'o', '\u00d8': 'O',     // Nordic
    '\u00df': 'ss',                   // German
    '\u0111': 'd', '\u0110': 'D',     // Croatian / Vietnamese
    '\u00e6': 'ae', '\u00c6': 'AE',
    '\u0153': 'oe', '\u0152': 'OE',
    '\u00fe': 'th', '\u00de': 'Th',   // Icelandic
};
export function sanitizeForPdf(text: string): string {
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u0130\u0131\u0141\u0142\u00d8\u00f8\u00df\u0110\u0111\u00c6\u00e6\u0152\u0153\u00de\u00fe]/g,
            ch => NON_DECOMPOSING_LATIN_MAP[ch] ?? ch);
}

// Eskimi brand colors - Visual Identity
const ESKIMI_COLORS = {
  // Primary Colors
  purple: '#8D5DD9',
  darkPurple: '#693BB1',
  midPurple: '#8D5DD9',
  lightPurple: '#DDC8FF',
  extraLightPurple: '#F4EDFF',
  
  // Blue Colors
  blue: '#8AB9FF',
  darkBlue: '#5A9CFF',
  lightBlue: '#C3DBFF',
  extraLightBlue: '#EAF2FF',
  
  // Text Colors
  darkBlack: '#1F2125',
  lightGrey: '#F1F3F6',
  darkGrey: '#9BA1AA',
  black: '#2C3139',
  grey: '#D2D6DC',
  white: '#FFFFFF',
  
  // Chart Colors
  red: '#FF6A6A',
  yellow: '#FFCF29',
  green: '#0E8A4C',
  extraLightYellow: '#FFF7DD',
  lightYellow: '#FFE99E',
  extraLightGreen: '#E7FAF1',
  lightGreen: '#B4EAD0',
  extraLightRed: '#FEEEEE',
  lightRed: '#FFB6B6',
  
  // Legacy mappings for easier refactoring
  primary: '#8D5DD9',
  secondary: '#0E8A4C',
  dark: '#1F2125',
  light: '#F1F3F6',
  accent: '#5A9CFF',
  text: '#2C3139',
  textLight: '#9BA1AA',
  border: '#D2D6DC',
  success: '#0E8A4C',
  danger: '#FF6A6A',
  warning: '#FFCF29'
};

interface AnalysisExportData {
  bulkJob: {
    id: string;
    promptId: string;
    promptName: string;
    createdAt: string;
    totalUrls: number;
    matchedUrls: number;
    unmatchedUrls: number;
    averageConfidence: number;
    totalCost?: number;
  };
  prompt: {
    name: string;
    description: string;
    prompt: string;
    type: 'brand-safety' | 'audience-targeting';
  };
  matchedSamples: Array<{
    url: string;
    reasoning: string;
    confidence: number;
    contentSummary: string;
  }>;
  unmatchedSamples: Array<{
    url: string;
    reasoning: string;
    confidence: number;
    contentSummary: string;
  }>;
}

export class AnalysisReportPDF {
  private pdf: jsPDF;
  private pageHeight: number;
  private pageWidth: number;
  private marginLeft: number = 20;
  private marginRight: number = 20;
  private marginTop: number = 25; // Increased to account for logo
  private marginBottom: number = 20;
  private currentY: number = 20;
  private lineHeight: number = 6;
  
  constructor() {
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    this.pageHeight = this.pdf.internal.pageSize.height;
    this.pageWidth = this.pdf.internal.pageSize.width;
  }

  private checkPageBreak(neededSpace: number = 30): void {
    if (this.currentY + neededSpace > this.pageHeight - this.marginBottom) {
      this.addPage();
    }
  }

  private logoDataUrl?: string;
  
  private addPage(): void {
    this.pdf.addPage();
    this.currentY = this.marginTop;
    this.addHeader(this.logoDataUrl);
  }

  private async addHeader(logoDataUrl?: string): Promise<void> {
    // Add Eskimi logo with proper aspect ratio
    let logoEndX = this.marginLeft;
    if (logoDataUrl) {
      try {
        // Logo dimensions should maintain aspect ratio
        // Actual logo is 1000x237 pixels = 4.22:1 ratio
        const logoHeight = 8;
        const logoWidth = logoHeight * 4.22; // Maintain exact aspect ratio
        this.pdf.addImage(logoDataUrl, 'PNG', this.marginLeft, 10, logoWidth, logoHeight);
        logoEndX = this.marginLeft + logoWidth;
      } catch (error) {
        console.error('Failed to add logo:', error);
        // Fallback to text logo
        this.addTextLogo();
        logoEndX = this.marginLeft + 30;
      }
    } else {
      // Fallback to text logo
      this.addTextLogo();
      logoEndX = this.marginLeft + 30;
    }
    
    // Add DeepContext text after logo
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(12);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkBlack.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkBlack.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkBlack.slice(5, 7), 16));
    this.pdf.text('DeepContext', logoEndX + 5, 15);
    
    // Add page number
    const pageNumber = (this.pdf as any).internal.getNumberOfPages();
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkGrey.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(5, 7), 16));
    this.pdf.setFontSize(10);
    this.pdf.text(
      `Page ${pageNumber}`,
      this.pageWidth - this.marginRight,
      15,
      { align: 'right' }
    );
    
    // Reset text color
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.black.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(5, 7), 16));
  }
  
  private addTextLogo(): void {
    // Fallback text logo with Eskimi purple
    this.pdf.setFillColor(parseInt(ESKIMI_COLORS.purple.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.purple.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.purple.slice(5, 7), 16));
    this.pdf.rect(this.marginLeft, 10, 30, 10, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(8);
    this.pdf.text('ESKIMI', this.marginLeft + 15, 16, { align: 'center' });
  }

  private addTitle(title: string): void {
    this.checkPageBreak();
    this.pdf.setFontSize(20);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkBlack.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkBlack.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkBlack.slice(5, 7), 16));
    // Center the title
    this.pdf.text(sanitizeForPdf(title), this.pageWidth / 2, this.currentY, { align: 'center' });
    this.currentY += 10;
  }

  private addSubtitle(subtitle: string): void {
    this.checkPageBreak();
    this.pdf.setFontSize(18);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.black.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(5, 7), 16));
    this.pdf.text(sanitizeForPdf(subtitle), this.marginLeft, this.currentY);
    this.currentY += 10;
  }

  private addSectionTitle(title: string): void {
    this.checkPageBreak();
    this.currentY += 5; // Add some spacing
    this.pdf.setFontSize(14);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.purple.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.purple.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.purple.slice(5, 7), 16));
    this.pdf.text(sanitizeForPdf(title), this.marginLeft, this.currentY);
    this.currentY += 8;
  }

  private addText(text: string, options?: { color?: string; size?: number; indent?: number }): void {
    const textColor = options?.color || ESKIMI_COLORS.text;
    const fontSize = options?.size || 10;
    const indent = options?.indent || 0;
    const safeText = sanitizeForPdf(text);
    
    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(parseInt(textColor.slice(1, 3), 16), parseInt(textColor.slice(3, 5), 16), parseInt(textColor.slice(5, 7), 16));
    
    const lines = this.pdf.splitTextToSize(
      safeText,
      this.pageWidth - this.marginLeft - this.marginRight - indent
    );
    
    lines.forEach((line: string) => {
      this.checkPageBreak();
      this.pdf.text(line, this.marginLeft + indent, this.currentY);
      this.currentY += this.lineHeight;
    });
  }

  private addStatBox(label: string, value: string | number, color: string): void {
    const boxWidth = 40;
    const boxHeight = 20;
    
    // Draw rounded rectangle
    this.pdf.setFillColor(parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16));
    this.pdf.roundedRect(this.marginLeft, this.currentY, boxWidth, boxHeight, 3, 3, 'F');
    
    // Add label
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(8);
    this.pdf.text(label, this.marginLeft + boxWidth/2, this.currentY + 6, { align: 'center' });
    
    // Add value
    this.pdf.setFontSize(14);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(value.toString(), this.marginLeft + boxWidth/2, this.currentY + 14, { align: 'center' });
    this.pdf.setFont('helvetica', 'normal');
  }

  private addCompactSummaryStats(data: AnalysisExportData): void {
    const matchRate = ((data.bulkJob.matchedUrls / data.bulkJob.totalUrls) * 100).toFixed(1);
    
    // Create a row of stat boxes - 4 metrics only
    const startY = this.currentY;
    const boxWidth = 40;
    const totalWidth = this.pageWidth - this.marginLeft - this.marginRight;
    const spacing = (totalWidth - (boxWidth * 4)) / 3; // Space between boxes
    
    // Total URLs
    this.addStatBox('Total URLs', data.bulkJob.totalUrls.toLocaleString(), ESKIMI_COLORS.purple);
    
    // Matched URLs
    this.marginLeft += boxWidth + spacing;
    this.currentY = startY;
    this.addStatBox('Matched', data.bulkJob.matchedUrls.toLocaleString(), ESKIMI_COLORS.green);
    
    // Unmatched URLs
    this.marginLeft += boxWidth + spacing;
    this.currentY = startY;
    this.addStatBox('Unmatched', data.bulkJob.unmatchedUrls.toLocaleString(), ESKIMI_COLORS.red);
    
    // Match Rate
    this.marginLeft += boxWidth + spacing;
    this.currentY = startY;
    this.addStatBox('Match Rate', `${matchRate}%`, ESKIMI_COLORS.darkBlue);
    
    // Reset margin and move Y
    this.marginLeft = 20;
    this.currentY = startY + 22;
  }

  private addPromptDefinition(prompt: AnalysisExportData['prompt']): void {
    this.addSectionTitle('Brand Blueprint Definition');
    
    // Prompt name and type
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(sanitizeForPdf(prompt.name), this.marginLeft, this.currentY);
    this.pdf.setFont('helvetica', 'normal');
    this.currentY += 6;
    
    // Brand Blueprint badge
    this.pdf.setFillColor(parseInt(ESKIMI_COLORS.primary.slice(1, 3), 16), parseInt(ESKIMI_COLORS.primary.slice(3, 5), 16), parseInt(ESKIMI_COLORS.primary.slice(5, 7), 16));
    this.pdf.roundedRect(this.marginLeft, this.currentY - 4, 30, 6, 2, 2, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(8);
    this.pdf.text('Brand Blueprint', this.marginLeft + 15, this.currentY - 0.5, { align: 'center' });
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.black.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(5, 7), 16));
    
    this.currentY += 8;
    
    // Description
    if (prompt.description) {
      this.addText(prompt.description, { color: ESKIMI_COLORS.textLight });
      this.currentY += 3;
    }
    
    // Calculate prompt text height to check if it fits on current page
    const promptTextWidth = this.pageWidth - this.marginLeft - this.marginRight - 16;
    const promptLines = this.pdf.splitTextToSize(sanitizeForPdf(prompt.prompt), promptTextWidth);
    const promptHeight = promptLines.length * this.lineHeight + 10;
    
    // Check if prompt box will fit on current page, if not start new page
    if (this.currentY + promptHeight > this.pageHeight - this.marginBottom) {
      this.addPage();
      this.currentY += 10; // Add some top margin on new page
    }
    
    // Now draw the prompt box
    const promptBoxY = this.currentY;
    
    // Set colors for the box
    this.pdf.setDrawColor(parseInt(ESKIMI_COLORS.grey.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.grey.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.grey.slice(5, 7), 16)); // Border color
    this.pdf.setFillColor(parseInt(ESKIMI_COLORS.extraLightPurple.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.extraLightPurple.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.extraLightPurple.slice(5, 7), 16)); // Light purple background
    
    // Check if we need to split the box across pages
    const availableHeight = this.pageHeight - this.marginBottom - promptBoxY;
    
    if (promptHeight <= availableHeight) {
      // Box fits on current page
      this.pdf.roundedRect(
        this.marginLeft,
        promptBoxY,
        this.pageWidth - this.marginLeft - this.marginRight,
        promptHeight,
        3,
        3,
        'FD'
      );
      
      // Add prompt text
      this.currentY = promptBoxY + 8;
      this.pdf.setFontSize(8.5);
      promptLines.forEach((line: string) => {
        this.pdf.text(line, this.marginLeft + 8, this.currentY);
        this.currentY += this.lineHeight;
      });
      this.currentY = promptBoxY + promptHeight + 5;
    } else {
      // Need to split across pages
      // Draw first part on current page
      const firstPageLines = Math.floor((availableHeight - 10) / this.lineHeight);
      const firstPageHeight = firstPageLines * this.lineHeight + 10;
      
      this.pdf.roundedRect(
        this.marginLeft,
        promptBoxY,
        this.pageWidth - this.marginLeft - this.marginRight,
        firstPageHeight,
        3,
        3,
        'FD'
      );
      
      // Add first part of text
      this.currentY = promptBoxY + 8;
      this.pdf.setFontSize(8.5);
      for (let i = 0; i < firstPageLines && i < promptLines.length; i++) {
        this.pdf.text(promptLines[i], this.marginLeft + 8, this.currentY);
        this.currentY += this.lineHeight;
      }
      
      // Continue on next page
      this.addPage();
      this.currentY += 10;
      
      // Draw remaining part
      const remainingLines = promptLines.slice(firstPageLines);
      const remainingHeight = remainingLines.length * this.lineHeight + 10;
      
      // Set colors again for new page
      this.pdf.setDrawColor(parseInt(ESKIMI_COLORS.grey.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.grey.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.grey.slice(5, 7), 16));
      this.pdf.setFillColor(parseInt(ESKIMI_COLORS.extraLightPurple.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(5, 7), 16));
      
      this.pdf.roundedRect(
        this.marginLeft,
        this.currentY,
        this.pageWidth - this.marginLeft - this.marginRight,
        remainingHeight,
        3,
        3,
        'FD'
      );
      
      // Add remaining text
      this.currentY += 8;
      this.pdf.setFontSize(8.5);
      remainingLines.forEach((line: string) => {
        this.pdf.text(line, this.marginLeft + 8, this.currentY);
        this.currentY += this.lineHeight;
      });
      this.currentY += 5;
    }
  }

  private addSimplePromptDefinition(promptText: string): void {
    const boxWidth = this.pageWidth - this.marginLeft - this.marginRight;
    const textPadding = 8;
    const textWidth = boxWidth - (textPadding * 2) - 4; // extra safety margin for non-ASCII font metrics

    this.pdf.setFontSize(8.5);
    const promptLines = this.pdf.splitTextToSize(sanitizeForPdf(promptText), textWidth);
    const lineSpacing = 5.5; // Reduced line spacing for more compact layout
    const boxPaddingTop = 6;
    const boxPaddingBottom = 6;
    
    // Calculate total height needed
    const totalHeight = promptLines.length * lineSpacing + boxPaddingTop + boxPaddingBottom;
    
    // Calculate available height on current page
    const availableHeight = this.pageHeight - this.marginBottom - this.currentY;
    
    if (totalHeight <= availableHeight) {
      // Everything fits on current page
      const promptBoxY = this.currentY;
      
      // Draw purple background box
      this.pdf.setDrawColor(parseInt(ESKIMI_COLORS.lightPurple.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.lightPurple.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.lightPurple.slice(5, 7), 16));
      this.pdf.setFillColor(parseInt(ESKIMI_COLORS.extraLightPurple.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(5, 7), 16));
      
      this.pdf.roundedRect(
        this.marginLeft,
        promptBoxY,
        boxWidth,
        totalHeight,
        3,
        3,
        'FD'
      );
      
      // Add text
      this.pdf.setTextColor(parseInt(ESKIMI_COLORS.black.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.black.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.black.slice(5, 7), 16));
      
      let textY = promptBoxY + boxPaddingTop;
      promptLines.forEach((line: string) => {
        this.pdf.text(line, this.marginLeft + textPadding, textY);
        textY += lineSpacing;
      });
      
      this.currentY = promptBoxY + totalHeight + 5;
    } else {
      // Need to split across pages
      const linesPerPage = Math.floor((availableHeight - boxPaddingTop - boxPaddingBottom) / lineSpacing);
      const firstPageLines = Math.max(1, linesPerPage);
      const firstPageHeight = firstPageLines * lineSpacing + boxPaddingTop + boxPaddingBottom;
      
      // Draw first part on current page
      this.pdf.setDrawColor(parseInt(ESKIMI_COLORS.lightPurple.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.lightPurple.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.lightPurple.slice(5, 7), 16));
      this.pdf.setFillColor(parseInt(ESKIMI_COLORS.extraLightPurple.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(5, 7), 16));
      
      this.pdf.roundedRect(
        this.marginLeft,
        this.currentY,
        boxWidth,
        firstPageHeight,
        3,
        3,
        'FD'
      );
      
      // Add first part of text
      this.pdf.setTextColor(parseInt(ESKIMI_COLORS.black.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.black.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.black.slice(5, 7), 16));
      
      let textY = this.currentY + boxPaddingTop;
      for (let i = 0; i < firstPageLines && i < promptLines.length; i++) {
        this.pdf.text(promptLines[i], this.marginLeft + textPadding, textY);
        textY += lineSpacing;
      }
      
      // Continue on next page
      this.addPage();
      this.currentY = this.marginTop + 5;
      
      // Draw remaining part
      const remainingLines = promptLines.slice(firstPageLines);
      const remainingHeight = remainingLines.length * lineSpacing + boxPaddingTop + boxPaddingBottom;
      
      // Reset colors for new page
      this.pdf.setDrawColor(parseInt(ESKIMI_COLORS.lightPurple.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.lightPurple.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.lightPurple.slice(5, 7), 16));
      this.pdf.setFillColor(parseInt(ESKIMI_COLORS.extraLightPurple.slice(1, 3), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(3, 5), 16), 
                           parseInt(ESKIMI_COLORS.extraLightPurple.slice(5, 7), 16));
      
      this.pdf.roundedRect(
        this.marginLeft,
        this.currentY,
        boxWidth,
        remainingHeight,
        3,
        3,
        'FD'
      );
      
      // Add remaining text
      this.pdf.setFontSize(8.5);
      this.pdf.setTextColor(parseInt(ESKIMI_COLORS.black.slice(1, 3), 16),
                           parseInt(ESKIMI_COLORS.black.slice(3, 5), 16),
                           parseInt(ESKIMI_COLORS.black.slice(5, 7), 16));
      
      textY = this.currentY + boxPaddingTop;
      remainingLines.forEach((line: string) => {
        this.pdf.text(line, this.marginLeft + textPadding, textY);
        textY += lineSpacing;
      });
      
      this.currentY += remainingHeight + 5;
    }
  }

  private addUrlSamplesPage(matchedSamples: any[], unmatchedSamples: any[]): void {
    // Title
    this.pdf.setFontSize(16);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkBlack.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkBlack.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkBlack.slice(5, 7), 16));
    this.pdf.text('URL Analysis Examples', this.pageWidth / 2, this.currentY, { align: 'center' });
    this.currentY += 10;
    
    // Column setup
    const gutter = 10; // Space between columns
    const totalWidth = this.pageWidth - this.marginLeft - this.marginRight;
    const columnWidth = (totalWidth - gutter) / 2;
    const leftColumnX = this.marginLeft;
    const rightColumnX = this.marginLeft + columnWidth + gutter;
    
    // Headers - clean text without icons for better alignment
    this.pdf.setFontSize(12);
    
    // Matched header - centered over the left column
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.green.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.green.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.green.slice(5, 7), 16));
    this.pdf.text('Matched URLs', leftColumnX + (columnWidth / 2), this.currentY, { align: 'center' });
    
    // Unmatched header - centered over the right column
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.red.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.red.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.red.slice(5, 7), 16));
    this.pdf.text('Unmatched URLs', rightColumnX + (columnWidth / 2), this.currentY, { align: 'center' });
    
    this.currentY += 8;
    
    // Add samples - dynamic based on available data
    const maxSamples = Math.max(matchedSamples.length, unmatchedSamples.length);
    const sampleHeight = maxSamples <= 10 ? 35 : maxSamples <= 20 ? 28 : 22; // Adjust height based on count
    
    for (let i = 0; i < maxSamples; i++) {
      const matchedSample = matchedSamples[i];
      const unmatchedSample = unmatchedSamples[i];
      const sampleY = this.currentY;
      
      // Matched sample
      if (matchedSample) {
        this.addCompactUrlSample(matchedSample, true, leftColumnX, sampleY, columnWidth, sampleHeight);
      }
      
      // Unmatched sample
      if (unmatchedSample) {
        this.addCompactUrlSample(unmatchedSample, false, rightColumnX, sampleY, columnWidth, sampleHeight);
      }
      
      // Adjust spacing based on sample count
      const spacing = maxSamples <= 10 ? 5 : maxSamples <= 20 ? 3 : 2;
      this.currentY = sampleY + sampleHeight + spacing;
      
      // Check if we need a new page
      if (i < maxSamples - 1 && this.currentY + sampleHeight > this.pageHeight - this.marginBottom) {
        this.addPage();
        this.currentY = 35;
      }
    }
  }

  private addCompactUrlSample(sample: any, isMatch: boolean, x: number, y: number, width: number, height: number = 32): void {
    const borderColor = isMatch ? ESKIMI_COLORS.green : ESKIMI_COLORS.red;
    const bgColor = isMatch ? ESKIMI_COLORS.extraLightGreen : ESKIMI_COLORS.extraLightRed;
    
    // Draw box with dynamic height
    const boxHeight = height;
    this.pdf.setDrawColor(parseInt(borderColor.slice(1, 3), 16), 
                         parseInt(borderColor.slice(3, 5), 16), 
                         parseInt(borderColor.slice(5, 7), 16));
    this.pdf.setFillColor(parseInt(bgColor.slice(1, 3), 16), 
                         parseInt(bgColor.slice(3, 5), 16), 
                         parseInt(bgColor.slice(5, 7), 16));
    
    this.pdf.roundedRect(x, y, width, boxHeight, 2, 2, 'FD');
    
    // Adjust font sizes based on box height
    const urlFontSize = boxHeight <= 22 ? 7 : 8;
    const reasoningFontSize = boxHeight <= 22 ? 6 : 7;
    const confidenceFontSize = boxHeight <= 22 ? 6 : 7;
    
    // URL (truncated for display, but full URL for link)
    this.pdf.setFontSize(urlFontSize);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkBlue.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkBlue.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkBlue.slice(5, 7), 16));
    
    // Clean URL for display: remove protocol, www, and query parameters
    let cleanUrl = sample.url;
    
    // Remove protocol (http:// or https://)
    cleanUrl = cleanUrl.replace(/^https?:\/\//, '');
    
    // Remove www.
    cleanUrl = cleanUrl.replace(/^www\./, '');
    
    // Remove query string parameters (everything after ?)
    const queryIndex = cleanUrl.indexOf('?');
    if (queryIndex > -1) {
      cleanUrl = cleanUrl.substring(0, queryIndex);
    }
    
    // Remove trailing slash if present
    cleanUrl = cleanUrl.replace(/\/$/, '');
    
    const maxUrlLength = boxHeight <= 22 ? 25 : 30;
    const displayUrl = cleanUrl.length > maxUrlLength ? 
      cleanUrl.substring(0, maxUrlLength) + '...' : 
      cleanUrl;
    
    // Add the text with a link to the full URL
    const textX = x + 3;
    const textY = y + (boxHeight <= 22 ? 4 : 5);
    
    this.pdf.textWithLink(sanitizeForPdf(displayUrl), textX, textY, { url: sample.url });
    
    // Confidence
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(confidenceFontSize);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkGrey.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(5, 7), 16));
    this.pdf.text(`${(sample.confidence * 100).toFixed(0)}%`, x + width - 5, textY, { align: 'right' });
    
    // Reasoning (truncated to fit)
    this.pdf.setFontSize(reasoningFontSize);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.black.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.black.slice(5, 7), 16));
    
    const textInset = 5;
    const reasoningLines = this.pdf.splitTextToSize(sanitizeForPdf(sample.reasoning), width - (textInset * 2) - 4);
    const maxLines = boxHeight <= 22 ? 2 : boxHeight <= 28 ? 3 : 4;
    const lineHeight = boxHeight <= 22 ? 4 : 5;
    const reasoningStartY = textY + (boxHeight <= 22 ? 5 : 6);
    
    for (let i = 0; i < Math.min(reasoningLines.length, maxLines); i++) {
      this.pdf.text(reasoningLines[i], x + textInset, reasoningStartY + (i * lineHeight));
    }
    
    if (reasoningLines.length > maxLines) {
      this.pdf.text('...', x + textInset, reasoningStartY + (maxLines * lineHeight));
    }
  }

  private addFooter(): void {
    this.currentY = this.pageHeight - 25;
    this.pdf.setDrawColor(parseInt(ESKIMI_COLORS.grey.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.grey.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.grey.slice(5, 7), 16));
    this.pdf.line(this.marginLeft, this.currentY, this.pageWidth - this.marginRight, this.currentY);
    this.currentY += 5;
    
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkGrey.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(5, 7), 16));
    this.pdf.text('© 2024 Eskimi. All rights reserved.', this.marginLeft, this.currentY);
    this.pdf.text('Eskimi DeepContext', this.pageWidth - this.marginRight, this.currentY, { align: 'right' });
  }

  private addUrlSample(sample: { url: string; reasoning: string; confidence: number; contentSummary: string }, isMatch: boolean): void {
    this.checkPageBreak(40);
    
    const borderColor = isMatch ? ESKIMI_COLORS.green : ESKIMI_COLORS.red;
    const bgColor = isMatch ? ESKIMI_COLORS.extraLightGreen : ESKIMI_COLORS.extraLightRed;
    
    const sampleStartY = this.currentY;
    
    const urlLines = this.pdf.splitTextToSize(sanitizeForPdf(sample.url), this.pageWidth - this.marginLeft - this.marginRight - 10);
    const summaryLines = this.pdf.splitTextToSize(sanitizeForPdf(sample.contentSummary), this.pageWidth - this.marginLeft - this.marginRight - 10);
    const reasoningLines = this.pdf.splitTextToSize(sanitizeForPdf(sample.reasoning), this.pageWidth - this.marginLeft - this.marginRight - 10);
    
    const totalHeight = (urlLines.length + summaryLines.length + reasoningLines.length + 3) * this.lineHeight + 15;
    
    // Draw border and background
    this.pdf.setDrawColor(parseInt(borderColor.slice(1, 3), 16), parseInt(borderColor.slice(3, 5), 16), parseInt(borderColor.slice(5, 7), 16));
    this.pdf.setFillColor(parseInt(bgColor.slice(1, 3), 16), parseInt(bgColor.slice(3, 5), 16), parseInt(bgColor.slice(5, 7), 16));
    this.pdf.roundedRect(
      this.marginLeft,
      sampleStartY,
      this.pageWidth - this.marginLeft - this.marginRight,
      totalHeight,
      3,
      3,
      'FD'
    );
    
    this.currentY = sampleStartY + 8;
    
    // URL
    this.pdf.setFont('helvetica', 'bold');
    this.addText(sample.url, { size: 10, indent: 5, color: ESKIMI_COLORS.dark });
    this.pdf.setFont('helvetica', 'normal');
    
    // Confidence badge
    const confidenceText = `${(sample.confidence * 100).toFixed(0)}% confidence`;
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(parseInt(ESKIMI_COLORS.darkGrey.slice(1, 3), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(3, 5), 16), 
                         parseInt(ESKIMI_COLORS.darkGrey.slice(5, 7), 16));
    this.pdf.text(confidenceText, this.pageWidth - this.marginRight - 5, sampleStartY + 8, { align: 'right' });
    
    this.currentY += 3;
    
    // Content summary
    if (sample.contentSummary) {
      this.pdf.setFont('helvetica', 'italic');
      this.addText(sample.contentSummary, { size: 9, indent: 5, color: ESKIMI_COLORS.textLight });
      this.pdf.setFont('helvetica', 'normal');
      this.currentY += 3;
    }
    
    // Reasoning
    this.addText(`Reasoning: ${sample.reasoning}`, { size: 9, indent: 5, color: ESKIMI_COLORS.text });
    
    this.currentY = sampleStartY + totalHeight + 5;
  }

  public async generateReport(data: AnalysisExportData): Promise<Blob> {
    // Try to load the Eskimi logo
    try {
      const response = await fetch('/eskimi-logo-medium.png');
      const blob = await response.blob();
      const reader = new FileReader();
      await new Promise<void>((resolve) => {
        reader.onloadend = () => {
          this.logoDataUrl = reader.result as string;
          resolve();
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Failed to load logo:', error);
    }
    
    // Add header to first page
    await this.addHeader(this.logoDataUrl);
    
    // Title with prompt name
    this.currentY = 30;
    this.addTitle(`${data.prompt.name} Brand Blueprint Overview`);
    
    // Summary Statistics (no title, compact)
    this.currentY += 5;
    this.addCompactSummaryStats(data);
    
    // Brand Blueprint Definition (just the text, no titles)
    this.currentY += 6;
    this.addSimplePromptDefinition(data.prompt.prompt);
    
    // URL Samples - Side by side on new page
    if (data.matchedSamples.length > 0 || data.unmatchedSamples.length > 0) {
      this.addPage();
      this.currentY = 35;
      this.addUrlSamplesPage(data.matchedSamples, data.unmatchedSamples);
    }
    
    // Footer on last page
    this.addFooter();
    
    // Return as blob
    return this.pdf.output('blob');
  }
}

// GUARD: do not replace this with a thin adapter that forwards to v2 — the
// "Export PDF" button must keep emitting the v1.12 rich layout. See bead
// deepcontext-7u2ox.
export const generateAnalysisReport = async (data: AnalysisExportData): Promise<void> => {
  const report = new AnalysisReportPDF();
  const blob = await report.generateReport(data);
  
  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // Different filename for prompt-level vs bulk job exports
  const isPromptExport = data.bulkJob.id.startsWith('prompt-');
  if (isPromptExport) {
    const sanitizedPromptName = data.prompt.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    link.download = `${sanitizedPromptName}-overview-${new Date().toISOString().split('T')[0]}.pdf`;
  } else {
    link.download = `analysis-report-${data.bulkJob.id}-${new Date().toISOString().split('T')[0]}.pdf`;
  }
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};